import { APP_LEGAL_LINKS } from "@/lib/brand";
import { listPracticeSocialLinks, type PracticeSocialLinks } from "@/lib/practice/social-links";

/**
 * The footer every template email ends with, as editable markdown.
 *
 * A practice edits this on the Email Templates page. It is one setting for the practice rather
 * than one per template, because the footer is the practice's identity and not a property of an
 * individual email.
 *
 * The values it can name — address, social links, phone — are NOT edited here. They come from
 * Settings -> Practice, and the footer references them as `{{variables}}`. So a practice composes
 * whatever footer it wants out of facts it maintains in one place.
 */

/**
 * What a practice sees before it edits anything: a traditional footer.
 *
 * Reproduces the shape Ultralight's own footer has always had — copyright, postal address, social
 * row, legal links — from the practice's own values. A practice that never opens the editor keeps
 * a correct footer.
 */
export const DEFAULT_EMAIL_FOOTER_MARKDOWN = [
  "© {{email.year}} {{practice.name}}",
  "{{practice.address}}",
  "{{practice.socials}}",
  `[Privacy Policy](${APP_LEGAL_LINKS.privacy}) • [Terms of Service](${APP_LEGAL_LINKS.terms})`,
].join("\n\n");

/**
 * Icon per network, as a root-relative path. The same glyphs the footer has always shown.
 *
 * A path, not a URL: the renderer prefixes the host, the way it already does for the logo. Baking
 * a host in here would put this module's import-time environment into the markdown.
 */
const SOCIAL_ICON_PATHS = {
  instagram: "/images/instagram.png",
  linkedin: "/images/linkedin.png",
  twitter: "/images/twitter.png",
} as const;

/** The practice fields the footer's variables resolve from. */
export interface FooterVariableSource {
  name: string;
  email?: string;
  phone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  socialLinks: PracticeSocialLinks;
}

/**
 * Resolves the variables a footer can name.
 *
 * Every value is a string, and an absent one resolves to `""` rather than throwing — a practice
 * that has no LinkedIn should get a footer without one, not a failed send.
 */
export function buildFooterVariableValues(
  practice: FooterVariableSource,
  now: Date = new Date(),
): Record<string, string> {
  const socials = listPracticeSocialLinks(practice.socialLinks);

  return {
    "practice.name": practice.name,
    "practice.email": practice.email ?? "",
    "practice.phone": practice.phone ?? "",
    "practice.address": formatPracticeAddress(practice),
    // Linked icons, written as markdown so the row stays editable and deletable like any other
    // line. The footer renderer maps `img` to a 24px `Img`.
    "practice.socials": socials
      .map((link) => `[![${link.label}](${SOCIAL_ICON_PATHS[link.network]})](${link.url})`)
      .join(" "),
    "practice.instagram": practice.socialLinks.instagram ?? "",
    "practice.linkedin": practice.socialLinks.linkedin ?? "",
    "practice.twitter": practice.socialLinks.twitter ?? "",
    "email.year": String(now.getFullYear()),
  };
}

/** Street, city, state, and ZIP on one line, the way a letter reads. */
export function formatPracticeAddress(practice: FooterVariableSource): string {
  const street = [practice.addressLine1?.trim(), practice.addressLine2?.trim()]
    .filter(Boolean)
    .join(", ");
  const cityState = [practice.city?.trim(), practice.state?.trim()].filter(Boolean).join(", ");
  const cityStateZip = [cityState, practice.zipCode?.trim()].filter(Boolean).join(" ");

  return [street, cityStateZip].filter(Boolean).join(" · ");
}
